// Итог документа по одному юридическому лицу: цепочка «начислено минус вычеты равно к выдаче за всё
// время» и под ней «выплачено» и «осталось выплатить».
//
// ## Почему цепочка
//
// Ряд равнозначных чисел человек читает как формулу, а финансовый директор проверяет её в уме:
// 261 516 − 130 000 − 92 725 − 10 590 = 28 201. Формула, которая не сходится, читается как «программа
// посчитала неверно», и дальше не верят ни одной цифре (`.claude/rules/ui-views.md`). Поэтому
// знак действия стоит в подписи, слагаемых четыре, как в образце заказчика, а крупная величина в
// ряду одна.
//
// ## Когда четырёх слагаемых не хватает
//
// Бывает, что вычеты больше начисленного: официальная часть 77 140 при начислено 52 700, и
// расчёт ведомости не уходит в минус. Тогда «52 700 − 0 − 77 140 − 0» даёт минус 24 440, а в
// итоге стоит 14 130, и цепочка не складывается в уме (замечание независимой проверки
// 01.10.2026: объяснение сидело мелкой строкой ниже, а читают цепочку). Разницу сервер отдаёт
// числом (`adjustment`), и она встаёт в цепочку ПЯТЫМ членом со своим знаком: «+ Не поместилось
// в начисление 38 570». Нет разницы, и цепочка прежняя, из четырёх слагаемых.
//
// ## Два числа, которые нельзя путать
//
// «К выдаче за всё время» — сумма итогов всех ведомостей, то самое число из его таблицы.
// «Осталось выплатить» — оно же минус то, что по ведомостям уже выплачено, и именно оно отвечает
// на вопрос «сколько сейчас платить». Поэтому оно и стоит отдельной крупной строкой: по итогу «за
// всё время» можно заплатить второй раз то, что уже переведено.
//
// Юрлиц несколько — несколько таких блоков, общего итога нет: разные кассы и разные дни выплаты.
//
// ## Ноль и «неизвестно» разные вещи
//
// Находка живого прохода по боевым данным 01.10.2026: у уволенного 18.09 ни по одной ведомости
// расчёта не было (не заполнен тариф), а итог крупно говорил «К выдаче за всё время 0» и «Осталось
// выплатить 0, платить нечего». Документ отдают затем, чтобы решить, сколько платить, и такой ноль
// был неправдой: не «ничего не положено», а «не посчитано». Поэтому, когда есть ведомости в работе
// без расчёта, а известного остатка к выплате нет, величины стоят прочерком, над ними сказано,
// почему, а «платить нечего» не пишется вовсе. Когда известная часть остатка есть, числа остаются
// (это правда о посчитанном), и сказано, что итог неполный и сколько ведомостей в него не вошло.
// «Платить нечего» и «всё выплачено»
// остаются только там, где посчитано всё и итог окончательный: у предварительного итога ноль
// называется «по посчитанному».
//
// Закрытые из книги месяцы неизвестными не считаются: расчёта по ним не было, но деньги в них
// записаны и выплачены (`суммыНеизвестны`).

import { Link } from 'react-router';

import { RiErrorWarningLine } from '@remixicon/react';

import type { PayslipPeriodTotals } from '@/shared/api/endpoints/payslip';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import * as Alert from '@/shared/ui/alert';
import { Block } from '@/shared/ui/block';
import * as LinkButton from '@/shared/ui/link-button';
import { Metric, MetricRow } from '@/shared/ui/metric';

import type { PayslipPeriodBlock } from '@/shared/api/endpoints/payslip';

import {
  итогНеизвестен,
  непосчитанноеВБлоках,
  поправкаИтога,
  сЗаглавной,
  type НепосчитанноеВБлоках,
} from './тексты';

/** Что стоит вместо числа, которого нет: ноль сказал бы «ничего не положено», а это неизвестно. */
const НЕИЗВЕСТНО = '—';

function запискаОсталось(
  итог: PayslipPeriodTotals,
  непосчитано: НепосчитанноеВБлоках,
  предварительно: boolean,
): string {
  if (итогНеизвестен(непосчитано, итог)) {
    return 'сколько платить, пока неизвестно';
  }
  if (итог.overpaid > 0) {
    return `выплачено на ${formatRubles(итог.overpaid)} больше, чем положено: система сама ничего не удерживает`;
  }
  if (итог.remaining === 0) {
    // «Всё выплачено» и «платить нечего» говорит только окончательный итог: у предварительного
    // ноль может оказаться первым приближением (не хватает месяца, ведомость в работе). Это
    // важнее всего у уволенного: за последний месяц ведомости может не быть вовсе, и «всё
    // выплачено» над пустым сентябрём читалось бы как «с ним рассчитались».
    if (предварительно) {
      return итог.to_issue !== 0
        ? 'по посчитанному всё выплачено, итог предварительный'
        : 'по посчитанному ноль, итог предварительный';
    }
    return итог.to_issue !== 0 ? 'всё выплачено' : 'платить нечего';
  }
  if (итог.remaining_unapproved > 0) {
    return `из них ${formatRubles(итог.remaining_unapproved)} по ведомостям в работе: там сумма ещё может измениться`;
  }
  // Часть ведомостей не посчитана: число правдиво о посчитанном и не больше. Сказано под самим
  // числом, а не только мелкой строкой ниже: по этому числу и платят.
  return непосчитано.без > 0
    ? 'по посчитанному, итог неполный'
    : 'к выдаче за всё время минус выплаченное';
}

export function ИтогЮрлица({
  итог,
  заголовок,
  фраза,
  блоки,
  предварительно,
  человек,
}: {
  итог: PayslipPeriodTotals;
  /** Название блока: юрлицо, когда их несколько, иначе общее слово. */
  заголовок: string;
  /** Период словами: «с ноября 2025 по март 2026». */
  фраза: string | null;
  /** Блоки этого юрлица: по ним считается, сколько ведомостей без расчёта и чем объяснена поправка. */
  блоки: readonly PayslipPeriodBlock[];
  /** Документ предварительный: «платить нечего» по нему не утверждается. */
  предварительно: boolean;
  /** Идентификатор человека: по нему ведёт ссылка туда, где заполняют тариф. */
  человек: string;
}) {
  const непосчитано = непосчитанноеВБлоках(блоки);
  // Есть ведомости в работе без расчёта, а известного остатка нет: денег не ноль, а неизвестно
  // (подробно в шапке файла).
  const неизвестен = итогНеизвестен(непосчитано, итог);
  const частично = !неизвестен && непосчитано.без > 0;
  const сумма = (копейки: number): string => (неизвестен ? НЕИЗВЕСТНО : formatKopecks(копейки));
  const тон = (копейки: number): 'приглушённый' | 'обычный' =>
    неизвестен || копейки === 0 ? 'приглушённый' : 'обычный';
  const поправка = неизвестен ? 0 : итог.adjustment;
  const названиеПоправки = поправкаИтога(поправка, блоки);
  // Тариф заполняют в назначении человека, и ссылка ведёт ровно туда: «не заполнен тариф» без пути
  // к исправлению оставляет человека искать, где это делается. Причины про часы и расчёт чинятся
  // в других местах (пробелы ниже называют их шаги), и ссылки для них нет.
  const проТариф = непосчитано.причины.some((причина) => /тариф/i.test(причина));
  return (
    <Block title={заголовок} note={фраза ?? undefined}>
      <div className='flex flex-col gap-5'>
        {неизвестен && (
          <Alert.Root variant='lighter' status='warning' size='small' role='status'>
            <Alert.Icon as={RiErrorWarningLine} />
            <div className='flex flex-col gap-1'>
              <p className='text-label-sm text-text-strong-950'>Итог пока не посчитан</p>
              {непосчитано.причины.length > 0 && (
                <ul className='ml-4 flex list-disc flex-col gap-0.5'>
                  {непосчитано.причины.map((причина) => (
                    <li key={причина} className='text-paragraph-xs text-text-sub-600'>
                      {сЗаглавной(причина)}
                    </li>
                  ))}
                </ul>
              )}
              {проТариф && (
                // Шаг, а не слова: на бумаге ему делать нечего.
                <div data-печать='скрыть'>
                  <LinkButton.Root variant='primary' size='small' underline asChild>
                    <Link
                      to={`/people/employees/r/${человек}?tab=assignments`}
                      className='цель-тача relative'
                    >
                      Открыть назначения человека: тариф заполняют там
                    </Link>
                  </LinkButton.Root>
                </div>
              )}
            </div>
          </Alert.Root>
        )}
        <MetricRow columns={поправка === 0 ? 5 : 6}>
          <Metric
            label='Начислено'
            value={сумма(итог.accruals_total)}
            tone={неизвестен ? 'приглушённый' : 'обычный'}
            note={итог.bonus > 0 ? 'по часам и тарифу, вместе с премией' : 'по часам и тарифу'}
          />
          <Metric
            label='− Аванс'
            value={сумма(итог.advance)}
            tone={тон(итог.advance)}
            note='выданный и удержанный'
          />
          <Metric
            label='− Официально'
            value={сумма(итог.official_total)}
            tone={тон(итог.official_total)}
            note='вычитается до остальных удержаний'
          />
          <Metric
            label='− Удержания'
            value={сумма(итог.deductions_total)}
            tone={тон(итог.deductions_total)}
            note='форма, билеты, обучение, штрафы'
          />
          {поправка !== 0 && (
            // Число без знака, а знак в подписи: так же стоят остальные члены. Подпись зависит от
            // причины (`поправкаИтога`): «не поместилось в начисление» верно только для каскада.
            <Metric
              label={названиеПоправки.подпись}
              value={formatKopecks(Math.abs(поправка))}
              note={названиеПоправки.пояснение}
            />
          )}
          <Metric
            label='= К выдаче за всё время'
            value={сумма(итог.to_issue)}
            tone={неизвестен ? 'приглушённый' : 'важный'}
            note={
              неизвестен
                ? 'не посчитано'
                : 'то же число, что «К выдаче за все время» в листе расчёта'
            }
          />
        </MetricRow>
        <MetricRow columns={2}>
          <Metric
            label='Уже выплачено'
            value={formatKopecks(итог.paid)}
            tone={итог.paid === 0 ? 'приглушённый' : 'обычный'}
            note={
              итог.paid === 0
                ? 'отметок о переводе нет'
                : 'отметки о переводе и выплаченные ведомости'
            }
          />
          <Metric
            label='Осталось выплатить'
            value={сумма(итог.remaining)}
            tone={неизвестен ? 'приглушённый' : 'главный'}
            note={запискаОсталось(итог, непосчитано, предварительно)}
          />
        </MetricRow>
        {частично && (
          // Числа выше правдивы о посчитанном; сколько ведомостей в них не вошло, говорится тут, а не
          // мелкой строкой у каждой: итог читают целиком.
          // Без склонений: «из 3 ведомостей» и «из 21 ведомости» по-русски разные слова, а так фраза
          // одна на любое число.
          <p className='text-paragraph-xs text-text-sub-600'>
            Ведомостей без расчёта: {непосчитано.без} из {непосчитано.всего}. В итог они не вошли,
            причины названы ниже, в таблице.
          </p>
        )}
      </div>
    </Block>
  );
}
